import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";
import { createRouter, createWebHistory } from "vue-router";

import adminRoutes from "./adminRoutes";
import authRoutes from "./authRoutes";
import chinaUsRoutes from "./chinaUsRoutes";
import mainRoutes from "./mainRoutes";
import makerCartRoutes from "./makerCartRoutes";
import teacherRoute from "./teacher";

import EncryptionService from "@/utils/EncryptionService";
import { setDocumentTitleFromRoute } from "@/utils/pageTitle";
import NoAuthPage from "../views/NoAuthPage.vue";
import NotFoundPage from "../views/NotFoundPage.vue";

const routes = [
  teacherRoute,
  ...authRoutes,
  ...mainRoutes,
  ...adminRoutes,
  ...chinaUsRoutes,
  ...makerCartRoutes,
  {
    path: "/403",
    name: "NoAuthPage",
    component: NoAuthPage,
    meta: { requiresAuth: false, title: "No Authorization" },
  },
  {
    path: "/404",
    name: "NotFoundPage",
    component: NotFoundPage,
    meta: { requiresAuth: false, title: "Page Not Found" },
  },
  // This should be the last route (catch-all for 404)
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "NotFoundPage" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Browser back/forward: restore previous scroll position
    if (savedPosition) {
      return savedPosition;
    }

    // In-page anchors (e.g. #section)
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }

    // Same path with only query/params change (tabs, filters): keep scroll
    if (from && to.path === from.path) {
      return false;
    }

    return {
      top: 0,
      left: 0,
      behavior: "smooth",
    };
  },
});

let tokenValidationTimeout;
let lastProfileRefresh = 0;
const PROFILE_REFRESH_INTERVAL = 5000;

const DISABLE_IS_PASSING_WALKTHROUGH =
  process.env.VUE_APP_DISABLE_IS_PASSING_WALKTHROUGH || false;

async function validateTokenPeriodically() {
  clearTimeout(tokenValidationTimeout);

  try {
    const token = await cookiesService.getCookie("authToken");
    if (!token) {
      return;
    }
  } catch (error) {
    console.error("Error during token validation:", error);
  }
}

async function refreshUserProfile(
  forceRefresh = false,
  requireValidProfile = false
) {
  try {
    const now = Date.now();
    if (!forceRefresh && now - lastProfileRefresh < PROFILE_REFRESH_INTERVAL) {
      return await cookiesService.getCookie("authToken");
    }

    const authToken = await cookiesService.getCookie("authToken");
    if (!authToken?.token) {
      return null;
    }

    const response = await userService.getProfile();
    const userProfile = response.data?.data;

    if (userProfile) {
      const updatedAuthToken = {
        ...authToken,
        ...userProfile,
      };
      await cookiesService.setCookie("authToken", updatedAuthToken);
      lastProfileRefresh = now;
      return updatedAuthToken;
    }

    lastProfileRefresh = now;
    return requireValidProfile ? null : authToken;
  } catch (error) {
    console.error("Error refreshing user profile:", error);
    return requireValidProfile
      ? null
      : await cookiesService.getCookie("authToken");
  }
}

router.beforeEach(async (to, from, next) => {
  let authToken = await cookiesService.getCookie("authToken");

  if (to.meta.redirectIfAuthenticated) {
    // Cookie presence alone cannot establish a valid session. Allow login
    // when validation fails, including temporary profile request failures.
    const validatedSession = authToken?.token
      ? await refreshUserProfile(true, true)
      : null;
    if (validatedSession) {
      next({ path: "/" });
      return;
    }
    next();
    return;
  }

  if (to.name === "OnboardingPage" && !authToken?.token) {
    next({ path: "/" });
    return;
  }

  if (authToken?.token) {
    const forceRefresh =
      from.name === "OnboardingPage" || to.name === "OnboardingPage";
    const refreshedAuthToken = await refreshUserProfile(forceRefresh);
    authToken = refreshedAuthToken;
  }

  const isPassingWalkthrough = authToken?.is_passing_walkthrough;
  const isAdmin = authToken?.user_group_id === 1;

  if (!DISABLE_IS_PASSING_WALKTHROUGH) {
    if (to.name === "OnboardingPage" && isPassingWalkthrough) {
      next({ path: "/" });
      return;
    }
  }

  if (
    authToken?.token &&
    !isPassingWalkthrough &&
    !isAdmin &&
    to.name !== "OnboardingPage"
  ) {
    next({ name: "OnboardingPage" });
    return;
  }

  if (to.name === "SubmissionSubmitPage") {
    const ymc_is_open = true;

    if (!ymc_is_open) {
      next({ name: "SubmissionPage" });
      return;
    }
  }

  if (to.meta.requiresAuth && !authToken?.token) {
    await checkAuthentication(next, authToken);
    return;
  } else {
    if (to.name === "SubmissionPage") {
      const url = new URL(window.location);

      if (url.searchParams.has("token")) {
        await checkAuthentication(next, authToken);
        return;
      }
    } else if (to.name === "MakerCartPage") {
      const url = new URL(window.location);

      if (url.searchParams.has("token")) {
        await checkAuthentication(next, authToken);
        return;
      }
    } else if (to.name === "SubmissionListPage") {
      const url = new URL(window.location);

      if (url.searchParams.has("token")) {
        await checkAuthentication(next, authToken);
        return;
      }
    } else if (to.name === "SubmissionSubmitPage") {
      const url = new URL(window.location);

      if (url.searchParams.has("token")) {
        await checkAuthentication(next, authToken);
        return;
      }
    }

    next();
  }
});

// eslint-disable-next-line
async function checkAuthentication(next, authToken) {
  const token = await cookiesService.getCookie("authToken");

  if (token?.token) {
    if (typeof tokenValidationTimeout === "undefined") {
      validateTokenPeriodically();
    }
    next();
    return;
  }

  next({ name: "LoginPage" });
}

router.afterEach((to) => {
  if (!(to.meta && to.meta.manageTitleManually)) {
    setDocumentTitleFromRoute(to);
  }

  const url = new URL(window.location.href);
  const encryptedMessage = url.searchParams.get("message");

  if (
    encryptedMessage &&
    EncryptionService.isValidEncryptedText(encryptedMessage)
  ) {
    try {
      const decryptedMessage = EncryptionService.decrypt(encryptedMessage);
      alert(decryptedMessage);
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url);
    } catch (error) {
      console.error("Failed to decrypt message:", error);
    }
  }
});

// Function to programmatically navigate to the 404 page
export const redirectTo404 = () => {
  router.push({ name: "NotFoundPage" });
};

export default router;
